import { GameState } from "../state";
import { Input } from "../../core/input";
import { len } from "../../core/math";
import { maxIntegrity, moveSpeed } from "../stats";

// Player movement, i-frames, regen, and standing-on-node repair.
export function updatePlayer(s: GameState, dt: number, input: Input): void {
  const p = s.player;
  const speed = moveSpeed(s.stats);

  p.vx = input.moveX * speed;
  p.vy = input.moveY * speed;
  p.x += p.vx * dt;
  p.y += p.vy * dt;

  p.x = Math.max(p.radius, Math.min(s.arenaW - p.radius, p.x));
  p.y = Math.max(p.radius, Math.min(s.arenaH - p.radius, p.y));

  if (len(input.moveX, input.moveY) > 0.01) {
    p.facing = Math.atan2(input.moveY, input.moveX);
  }

  if (p.invuln > 0) p.invuln -= dt;

  // Regen
  const maxI = maxIntegrity(s.stats);
  if (s.stats.regenPerSec > 0 && p.integrity < maxI) {
    p.integrity = Math.min(maxI, p.integrity + s.stats.regenPerSec * dt);
  }

  // Nodes are gone-is-gone: the only way to rebuild a downed node (or top up a
  // damaged one) is for PCKT to SIT IN it. A slow passive self-heal exists only
  // as an upgrade (nodeRegenPerSec) and only for nodes that are still online, so
  // a destroyed node never comes back on its own and the regen can be overrun.
  for (const n of s.nodes) {
    const d = len(p.x - n.x, p.y - n.y);
    const sitting = d < n.radius; // PCKT centered inside the node
    if (sitting) {
      const before = n.online;
      n.stability = Math.min(n.maxStability, n.stability + NODE_REPAIR_RATE * dt);
      if (n.stability > 25) n.online = true;
      if (n.stability > 80) n.warned = false; // re-arm the warning after recovery
      if (!before && n.online) {
        s.ring(n.x, n.y, "#79dac8", 20, 240);
        s.addFloat(n.x, n.y - n.radius, "REBUILT", "#79dac8", 20);
        s.addBandwidth(4);
      }
    } else if (n.online && s.stats.nodeRegenPerSec > 0 && n.stability < n.maxStability) {
      n.stability = Math.min(n.maxStability, n.stability + s.stats.nodeRegenPerSec * dt);
    }
  }
}

/** Stability per second restored while PCKT sits in a node. */
const NODE_REPAIR_RATE = 26;
